const DB_NAME = 'foodops-receiving-offline';
const STORE = 'commands';
const VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) return reject(new Error('Offline storage is unavailable.'));
    const request = window.indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('identity', 'identity');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Offline storage could not be opened.'));
  });
}

function transaction(mode, operation) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode), store = tx.objectStore(STORE);
    let value;
    try { value = operation(store); } catch (error) { db.close(); reject(error); return; }
    tx.oncomplete = () => { db.close(); resolve(value); };
    tx.onerror = () => { db.close(); reject(tx.error || new Error('Offline storage failed.')); };
    tx.onabort = tx.onerror;
  }));
}

export const identityKey = ({ userId, orgId, locationId, department }) => JSON.stringify([userId, orgId, locationId, department || '']);

export async function enqueue(identity, command) {
  if (!identity?.userId || !identity?.orgId || !identity?.locationId) throw new Error('A complete signed-in identity is required.');
  if (!['save_line', 'submit'].includes(command?.kind) || !command?.command_id || !command?.receipt_id) throw new Error('Unsupported offline command.');
  if (command.file || command.blob || command.bytes) throw new Error('Evidence files cannot be queued offline.');
  const id = `${identityKey(identity)}:${command.command_id}`;
  const row = { ...command, id, identity: identityKey(identity), state: 'queued', queued_at: new Date().toISOString(), attempts: 0 };
  await transaction('readwrite', store => store.put(row));
  return row;
}

export async function list(identity) {
  const key = identityKey(identity);
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly'), index = tx.objectStore(STORE).index('identity'), request = index.getAll(key);
    request.onsuccess = () => resolve(request.result.sort((a, b) => a.queued_at.localeCompare(b.queued_at)));
    request.onerror = () => reject(request.error || new Error('Offline queue could not be read.'));
    tx.oncomplete = () => db.close(); tx.onabort = () => db.close();
  });
}

export async function remove(id) { await transaction('readwrite', store => store.delete(id)); }
export async function update(row) { await transaction('readwrite', store => store.put(row)); return row; }
export async function purgeIdentity(identity) { const rows = await list(identity); await Promise.all(rows.map(row => remove(row.id))); }

export async function purgeReceivingDeviceState(identity) {
  await purgeIdentity(identity);
  const required = [identity.userId, identity.orgId, identity.locationId].filter(Boolean).map(value => String(value));
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index) || '';
    if (key.includes('receiving-') && required.every(value => key.includes(value))) sessionStorage.removeItem(key);
  }
}

export async function syncOne(row, api) {
  if (row.state === 'conflict' || row.state === 'auth') throw new Error('Resolve this queued command before syncing.');
  await update({ ...row, state: 'syncing', attempts: row.attempts + 1, last_attempt_at: new Date().toISOString() });
  try {
    const result = row.kind === 'save_line'
      ? await api.saveLine(row.receipt_id, row.line_index, row.body)
      : await api.submit(row.receipt_id, row.body);
    await remove(row.id); return { state: 'done', result };
  } catch (error) {
    const state = error.status === 409 ? 'conflict' : [401, 403].includes(error.status) ? 'auth' : 'queued';
    const next = { ...row, state, attempts: row.attempts + 1, last_error: error.message,
      server: error.status === 409 ? error.detail?.current || null : null, last_attempt_at: new Date().toISOString() };
    await update(next); return next;
  }
}
